import {memo} from 'react';
import {Palette} from 'hubbl-shared';
import styled from 'styled-components';
import {FontConfig} from '../../../../../constants/FontConfig';

// Styled Components for Horizontal Progress Bars
const ProgressBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const ProgressBarItem = styled.div<{isClickable: boolean}>`
  position: relative;
  width: 100%;
  height: 40px;
  background: ${Palette.primarySubtleGreen};
  border-radius: 20px;
  overflow: hidden;
  cursor: ${props => (props.isClickable ? 'pointer' : 'default')};
  transition: all 0.2s ease;
  border: 0.5px solid ${Palette.almostBlackTransparent10};

  &:hover {
    ${props =>
      props.isClickable &&
      `
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      border-color: ${Palette.primaryTerraCotta};
    `}
  }
`;

const ProgressBarFill = styled.div<{width: number; color: string}>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${props => props.width}%;
  background: ${props => props.color};
  border-radius: 20px;
  transition: width 0.3s ease;
`;

const ProgressBarLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  z-index: 2;
  font-family: ${FontConfig.Heading5.fontFamily};
  font-size: ${FontConfig.Heading5.fontSize}px;
  font-weight: ${FontConfig.Heading5.fontWeight};
  letter-spacing: ${FontConfig.Heading5.letterSpacing}px;
  color: ${Palette.almostBlack};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 100px);
`;

const ProgressBarCount = styled.div`
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  z-index: 2;
  font-family: ${FontConfig.Heading6.fontFamily};
  font-size: ${FontConfig.Heading6.fontSize}px;
  font-weight: ${FontConfig.Heading6.fontWeight};
  letter-spacing: ${FontConfig.Heading6.letterSpacing}px;
  color: ${Palette.almostBlack500};
  background: ${Palette.white};
  padding: 2px 8px;
  border-radius: 12px;
  border: 1px solid ${Palette.almostBlackTransparent10};
`;

const ProgressBarPercentage = styled.div`
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  z-index: 2;
  font-family: ${FontConfig.Heading6.fontFamily};
  font-size: ${FontConfig.Heading6.fontSize}px;
  font-weight: ${FontConfig.Heading6.fontWeight};
  letter-spacing: ${FontConfig.Heading6.letterSpacing}px;
  color: ${Palette.almostBlack500};
  background: ${Palette.white};
  padding: 2px 8px;
  border-radius: 12px;
  border: 1px solid ${Palette.almostBlackTransparent10};
`;

export interface ProgressBarData {
  id: string;
  label: string;
  count: number;
  percentage?: number;
  color: string;
  onClick?: () => void;
}

interface HorizontalProgressBarsProps {
  data: ProgressBarData[];
  showPercentage?: boolean;
  maxCount?: number;
}

export const HorizontalProgressBars = memo(
  ({data, showPercentage = false, maxCount}: HorizontalProgressBarsProps) => {
    // Calculate the maximum count for percentage calculation if not provided
    const calculatedMaxCount =
      maxCount || Math.max(...data.map(item => item.count));

    return (
      <ProgressBarContainer>
        {data.map((item, index) => {
          const fillPercentage =
            calculatedMaxCount > 0
              ? (item.count / calculatedMaxCount) * 100
              : 0;

          return (
            <ProgressBarItem
              key={item.id}
              isClickable={!!item.onClick}
              onClick={item.onClick}>
              <ProgressBarFill width={fillPercentage} color={item.color} />
              <ProgressBarLabel>{item.label}</ProgressBarLabel>
              {showPercentage && item.percentage !== undefined ? (
                <ProgressBarPercentage>
                  {item.percentage.toFixed(1)}%
                </ProgressBarPercentage>
              ) : (
                <ProgressBarCount>{item.count}</ProgressBarCount>
              )}
            </ProgressBarItem>
          );
        })}
      </ProgressBarContainer>
    );
  },
);
